import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { IntlShape } from 'react-intl/src/types';
import styled from 'styled-components';

import { RatingsByVenueEntryTotal, RatingsByVenueInsight } from 'apis/merchant-gateway/insights/ratings-by-venue/types';
import { InsightResponsePaginated } from 'modules/insights-v2/types';

import { SortOrder, SortState } from '../../tables/types';

export enum RatingsByVenueTableColumnIDs {
  VENUE_NAME = 'VENUE_NAME',
  AVERAGE_RATING = 'AVERAGE_RATING',
  LIFETIME_RATING = 'LIFETIME_RATING',
  POSITIVE_RATINGS_COUNT = 'POSITIVE_RATINGS_COUNT',
  NEGATIVE_RATINGS_COUNT = 'NEGATIVE_RATINGS_COUNT',
  TOTAL_RATINGS_COUNT = 'TOTAL_RATINGS_COUNT',
}
export type RatingsByVenueTableSortableColumnIDs =
  | RatingsByVenueTableColumnIDs.AVERAGE_RATING
  | RatingsByVenueTableColumnIDs.LIFETIME_RATING;

type ColumnDef = {
  width: number;
  id: RatingsByVenueTableColumnIDs;
  label: string;
  render: (record: ReviewTableItem) => React.ReactNode;
};
export type ReviewTableItem = RatingsByVenueEntryTotal & { id: string; venueName: string };
export type ReviewsTableProps = {
  className?: string;
  fetchingNextPage: boolean;
  fetchingPreviousPage: boolean;
  onFetchNextPage?: () => void;
  onFetchPreviousPage?: () => void;
  items: ReviewTableItem[];
  sort?: SortState<RatingsByVenueTableSortableColumnIDs>;
  columns: ColumnDef[];
};

const TrimmedText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const getColumnWidth = (columns: { widthRatio?: number }[], column: { widthRatio?: number }) => {
  const widthRatiosSum = columns.reduce((acc, { widthRatio }) => acc + (widthRatio ?? 1), 0);
  const percentsPerRatio = 100 / widthRatiosSum;

  const columnWidthRatio = column.widthRatio ?? 1;
  const columnWidthPercentage = percentsPerRatio * columnWidthRatio;

  return columnWidthPercentage;
};
const getColumnDefs = ({
  handleSort,
  intl,
}: {
  handleSort?: (sort: SortState<RatingsByVenueTableSortableColumnIDs>) => void;
  intl: IntlShape;
}): ColumnDef[] => {
  const columns = [
    {
      id: RatingsByVenueTableColumnIDs.VENUE_NAME,
      label: intl.formatMessage({ id: 'insights.ratings-and-reviews.ratings-by-venue-table-column.venue' }),
      render: (record: ReviewTableItem) => <TrimmedText>{record.venueName}</TrimmedText>,
      widthRatio: 2,
    },
    {
      id: RatingsByVenueTableColumnIDs.AVERAGE_RATING,
      label: intl.formatMessage({ id: 'insights.ratings-and-reviews.ratings-by-venue-table-column.rating' }),
      render: (record: ReviewTableItem) => intl.formatNumber(record.averageRating, { maximumFractionDigits: 1 }),
      enableSort: true,
      handleSort: (sortOrder: SortOrder) =>
        handleSort?.({ sortBy: RatingsByVenueTableColumnIDs.AVERAGE_RATING, sortOrder }),
      widthRatio: 0.8,
    },
    {
      id: RatingsByVenueTableColumnIDs.LIFETIME_RATING,
      label: intl.formatMessage({ id: 'insights.ratings-and-reviews.ratings-by-venue-table-column.lifetime-rating' }),
      render: (record: ReviewTableItem) => record.lifetimeRating,
      enableSort: true,
      handleSort: (sortOrder: SortOrder) =>
        handleSort?.({ sortBy: RatingsByVenueTableColumnIDs.LIFETIME_RATING, sortOrder }),
    },
    {
      id: RatingsByVenueTableColumnIDs.POSITIVE_RATINGS_COUNT,
      label: intl.formatMessage({
        id: 'insights.ratings-and-reviews.ratings-by-venue-table-column.positive-ratings-count',
      }),
      render: (record: ReviewTableItem) => record.positiveRatings,
    },
    {
      id: RatingsByVenueTableColumnIDs.NEGATIVE_RATINGS_COUNT,
      label: intl.formatMessage({
        id: 'insights.ratings-and-reviews.ratings-by-venue-table-column.negative-ratings-count',
      }),
      render: (record: ReviewTableItem) => record.negativeRatings,
    },
    {
      id: RatingsByVenueTableColumnIDs.TOTAL_RATINGS_COUNT,
      label: intl.formatMessage({
        id: 'insights.ratings-and-reviews.ratings-by-venue-table-column.total-ratings-count',
      }),
      render: (record: ReviewTableItem) => record.totalRatings,
    },
  ];

  return columns.map(column => ({
    ...column,
    width: getColumnWidth(columns, column),
  }));
};

export const useRatingsByVenueTableProps = ({
  sort,
  setSort: handleSort,
  ratingsByVenueInsights,
}: {
  sort: SortState<RatingsByVenueTableSortableColumnIDs>;
  setSort: (sort: SortState<RatingsByVenueTableSortableColumnIDs>) => void;
  ratingsByVenueInsights: InsightResponsePaginated<RatingsByVenueInsight>;
}): ReviewsTableProps => {
  const intl = useIntl();
  const {
    data: performanceByVenueData,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
  } = ratingsByVenueInsights;

  const items: ReviewTableItem[] = useMemo(
    () =>
      performanceByVenueData?.pages.reduce(
        (pagesRes, page): ReviewTableItem[] =>
          page.data.rating.split?.reduce(
            (res, { label: id, secondaryLabel: venueName, total }): ReviewTableItem[] => [
              ...res,
              { ...total, venueName, id },
            ],
            pagesRes,
          ) ?? [],
        [],
      ) ?? [],
    [performanceByVenueData?.pages],
  );

  const columns = useMemo(() => getColumnDefs({ handleSort, intl }), [handleSort, intl]);

  const tableProps = {
    fetchingNextPage: isFetchingNextPage,
    fetchingPreviousPage: isFetchingPreviousPage,
    items,
    sort,
    onFetchNextPage: hasNextPage ? fetchNextPage : undefined,
    onFetchPreviousPage: hasPreviousPage ? fetchPreviousPage : undefined,
    columns,
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => tableProps, [...Object.values(tableProps)]);
};
